import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { describeMatch, errorInfo, isDuplicateRefusal, newIdempotencyKey } from "../lib/collaborators";
import { MAX_ROWS, buildRow, decodeFile, mapHeaders, parseCsv, templateCsv } from "../lib/collaboratorImport";
import useCollabReferentials from "../hooks/useCollabReferentials";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, CheckCircle2, Download, FileSpreadsheet, Info, Loader2, RotateCcw, Upload, XCircle } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

// Result of one row once sent: created | duplicate | error. Each row keeps its own idempotency key, so retrying never
// creates a person twice (a row already created is simply replayed by the server).
function ResultBadge({ result }) {
  if (!result) return <span className="text-xs text-slate-500">À importer</span>;
  if (result.state === "created") return <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 size={13} /> Créée</span>;
  if (result.state === "duplicate") return <span className="inline-flex items-center gap-1 text-xs text-amber-800"><AlertTriangle size={13} /> Doublon possible</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-red-700"><XCircle size={13} /> Erreur</span>;
}

export default function CollaboratorImportPage() {
  const navigate = useNavigate();
  const refs = useCollabReferentials();
  const fileInput = useRef(null);
  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null); // { headerUnknown, missingName, rows: [{line, cells, key}] }
  const [fileError, setFileError] = useState("");
  const [results, setResults] = useState({}); // line -> { state, reference?, id?, message?, matches? }
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const rows = useMemo(() => {
    if (!parsed || refs.status !== "ready") return [];
    return parsed.rows.map((r) => ({ ...r, ...buildRow(r.cells, parsed.mapping, refs, today()) }));
  }, [parsed, refs]);
  const valid = rows.filter((r) => !r.errors.length);
  const invalid = rows.filter((r) => r.errors.length);
  const pending = valid.filter((r) => results[r.line]?.state !== "created");
  const done = Object.keys(results).length > 0;
  const counts = Object.values(results).reduce((acc, r) => ({ ...acc, [r.state]: (acc[r.state] || 0) + 1 }), {});

  const readFile = async (file) => {
    setFileError(""); setParsed(null); setResults({}); setProgress(0);
    if (!file) return;
    setFileName(file.name);
    if (file.size > 2 * 1024 * 1024) { setFileError("Fichier trop volumineux (2 Mo maximum)."); return; }
    const table = parseCsv(decodeFile(await file.arrayBuffer()));
    if (table.length < 2) { setFileError("Le fichier ne contient aucune ligne de données sous l'en-tête."); return; }
    const { mapping, unknown } = mapHeaders(table[0]);
    if (mapping.first_name === undefined && mapping.last_name === undefined) {
      setFileError("Colonnes « prenom » et « nom » introuvables : vérifiez la première ligne ou partez du modèle.");
      return;
    }
    const data = table.slice(1);
    if (data.length > MAX_ROWS) { setFileError(`${data.length} lignes : l'import est limité à ${MAX_ROWS} lignes par fichier.`); return; }
    setParsed({ mapping, unknown, rows: data.map((cells, i) => ({ line: i + 2, cells, key: newIdempotencyKey() })) });
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([templateCsv()], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = "modele-import-collaborateurs.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const run = async () => {
    setRunning(true);
    const queue = pending;
    let index = 0;
    for (const row of queue) {
      let result;
      try {
        const { data } = await api.post("/collaborators", row.payload, { headers: { "Idempotency-Key": row.key } });
        result = { state: "created", id: data.id, reference: data.reference };
      } catch (err) {
        const info = errorInfo(err);
        result = isDuplicateRefusal(info)
          ? { state: "duplicate", message: info.data.msg, matches: info.data.matches || [] }
          : { state: "error", message: info.data?.msg || errorMessage(err, "Refusé par le serveur") };
      }
      setResults((old) => ({ ...old, [row.line]: result }));
      index += 1;
      setProgress(Math.round((index / queue.length) * 100));
    }
    setRunning(false);
    toast.success("Import terminé");
  };

  return (
    <div className="space-y-4" data-testid="collab-import-page">
      <Link to="/collaborateurs" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Retour aux collaborateurs</Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Importer des collaborateurs</h1>
        <p className="text-sm text-slate-500">Ajoutez plusieurs personnes à partir d'un fichier CSV (Excel : « Enregistrer sous » → CSV).</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          <Card className="p-4 sm:p-5 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Button type="button" onClick={() => fileInput.current?.click()} disabled={running} data-testid="btn-import-pick">
                <Upload size={15} className="mr-1.5" /> Choisir un fichier CSV
              </Button>
              <input ref={fileInput} type="file" accept=".csv,text/csv" className="sr-only" tabIndex={-1} data-testid="input-import-file"
                onChange={(e) => { readFile(e.target.files?.[0]); e.target.value = ""; }} />
              <Button type="button" variant="outline" onClick={downloadTemplate} data-testid="btn-import-template"><Download size={15} className="mr-1.5" /> Télécharger le modèle</Button>
            </div>
            {fileName && <p className="text-sm text-slate-600 flex items-center gap-1.5"><FileSpreadsheet size={15} className="text-slate-400" /> {fileName}</p>}
            {fileError && <p role="alert" className="text-sm text-red-700" data-testid="import-file-error">{fileError}</p>}
            {refs.status === "error" && <p role="alert" className="text-sm text-red-700">Impossible de charger les types de relation et les services. <button className="underline" onClick={refs.reload}>Réessayer</button></p>}
            {parsed?.unknown?.length > 0 && (
              <p className="text-xs text-slate-500">Colonnes ignorées : {parsed.unknown.join(", ")}.</p>
            )}
          </Card>

          {parsed && rows.length > 0 && (
            <Card className="p-4 sm:p-5 space-y-3" data-testid="import-preview">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-700">
                  <strong>{rows.length}</strong> ligne{rows.length > 1 ? "s" : ""} · <span className="text-emerald-700">{valid.length} prête{valid.length > 1 ? "s" : ""}</span>
                  {invalid.length > 0 && <> · <span className="text-red-700">{invalid.length} à corriger dans le fichier</span></>}
                </div>
                <Button type="button" onClick={run} disabled={running || !pending.length} data-testid="btn-import-run">
                  {running ? <Loader2 size={14} className="animate-spin mr-1.5" /> : done ? <RotateCcw size={14} className="mr-1.5" /> : null}
                  {running ? "Import en cours…" : done ? `Réessayer ${pending.length} ligne${pending.length > 1 ? "s" : ""}` : `Importer ${valid.length} personne${valid.length > 1 ? "s" : ""}`}
                </Button>
              </div>
              {running && <Progress value={progress} aria-label="Avancement de l'import" />}
              {done && !running && (
                <p className="text-sm text-slate-700" data-testid="import-summary">
                  {counts.created || 0} créée{(counts.created || 0) > 1 ? "s" : ""} · {counts.duplicate || 0} doublon{(counts.duplicate || 0) > 1 ? "s" : ""} possible{(counts.duplicate || 0) > 1 ? "s" : ""} · {counts.error || 0} erreur{(counts.error || 0) > 1 ? "s" : ""}
                </p>
              )}
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 max-h-[60vh] overflow-y-auto">
                {rows.map((r) => {
                  const result = results[r.line];
                  return (
                    <li key={r.line} className="px-3 py-2.5 text-sm flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3" data-testid="import-row">
                      <span className="text-xs text-slate-400 w-14 shrink-0">Ligne {r.line}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-slate-900 break-words">
                          {result?.state === "created" ? <Link to={`/collaborateurs/${result.id}`} className="text-jamora hover:underline">{r.display || "Sans nom"}</Link> : r.display || "Sans nom"}
                          {result?.reference && <span className="ml-2 text-xs text-slate-500">{result.reference}</span>}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {[r.payload.job_title, refs.relationLabel(r.payload.relation_type), r.payload.status === "active" ? "Actif" : "Préinscrit", refs.serviceName(r.payload.service_id), r.payload.contacts.phone].filter(Boolean).join(" · ")}
                        </span>
                        {r.errors.length > 0 && <span className="block text-xs text-red-700 mt-0.5">{r.errors.join(" · ")}</span>}
                        {result?.state === "duplicate" && (
                          <span className="block text-xs text-amber-800 mt-0.5">
                            {result.message} {result.matches.map(describeMatch).join(" ; ")}. À créer depuis le formulaire si ce n'est pas un doublon.
                          </span>
                        )}
                        {result?.state === "error" && <span className="block text-xs text-red-700 mt-0.5">{result.message}</span>}
                      </span>
                      <span className="shrink-0">{r.errors.length ? <span className="text-xs text-red-700">Non importée</span> : <ResultBadge result={result} />}</span>
                    </li>
                  );
                })}
              </ul>
              {done && !running && (counts.created || 0) > 0 && (
                <Button variant="outline" onClick={() => navigate("/collaborateurs")}>Voir l'annuaire</Button>
              )}
            </Card>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4">
          <Card className="p-4 space-y-2 bg-slate-50 border-slate-200">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Info size={15} className="text-jamora" /> Colonnes reconnues</div>
            <p className="text-xs text-slate-600">prenom, nom, fonction, type_relation, statut (actif ou préinscrit), date_entree (obligatoire si actif), service, email, telephone, whatsapp. Seuls le prénom ou le nom sont obligatoires.</p>
          </Card>
          <Card className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><AlertTriangle size={15} className="text-jamora" /> Doublons</div>
            <p className="text-xs text-slate-600">Chaque ligne passe par les mêmes contrôles que le formulaire. Une personne qui ressemble à une fiche existante n'est pas créée : vérifiez-la, puis créez-la à la main si besoin. Relancer l'import ne crée jamais deux fois la même ligne.</p>
          </Card>
        </aside>
      </div>
    </div>
  );
}
